import type {GradientTextureNode} from "#material/cycles/types"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {TexGradientSockets} from "#material/nodes/texture/tex-gradient/definition"
import {texGradientDefinition} from "#material/nodes/texture/tex-gradient/definition"
import {cyclesGradientType} from "#material/nodes/texture/tex-gradient/helpers"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import {threeRGBColor} from "#material/three/three-utils"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"

@registerMaterialGraphNode
export class TexGradient extends MaterialNode<TexGradientSockets> {
    public override get definition() {
        return texGradientDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<TexGradientSockets>> => {
        const type = this.parameters.settings.gradientType ?? "LINEAR"
        const vector = await evaluator.get(this.parameters.inputs.vector)
        const vectorValue = vector?.toVec3() ?? TSL.uv(0)

        const black = threeRGBColor({r: 0.0, g: 0.0, b: 0.0})
        const white = threeRGBColor({r: 1.0, g: 1.0, b: 1.0})

        const xComponent = vectorValue.x
        const yComponent = vectorValue.y

        if (type === "LINEAR") {
            const color = TSL.mix(black, white, xComponent).toColor()
            return {color, fac: xComponent}
        } else if (type === "QUADRATIC") {
            const quadraticComponent = TSL.pow(xComponent, 2)
            const color = TSL.mix(black, white, quadraticComponent).toColor()
            return {color, fac: quadraticComponent}
        } else if (type === "EASING") {
            const easingComponent = TSL.smoothstep(0.0, 1.0, xComponent)
            const color = TSL.mix(black, white, easingComponent).toColor()
            return {color, fac: easingComponent}
        } else if (type === "DIAGONAL") {
            const sumXY = TSL.add(xComponent, yComponent)
            const diagonalComponent = TSL.div(sumXY, 2)
            const color = TSL.mix(black, white, diagonalComponent).toColor()
            return {color, fac: diagonalComponent}
        } else if (type === "SPHERICAL") {
            const length = TSL.length(vectorValue)
            const inverseLength = TSL.oneMinus(length)
            const color = TSL.mix(black, white, inverseLength).toColor()
            return {color, fac: inverseLength}
        } else if (type === "QUADRATIC_SPHERE") {
            const length = TSL.length(vectorValue)
            const quadraticLength = TSL.pow(length, 2)
            const inverseQuadraticLength = TSL.oneMinus(quadraticLength)
            const color = TSL.mix(black, white, inverseQuadraticLength).toColor()
            return {color, fac: inverseQuadraticLength}
        } else if (type === "RADIAL") {
            const angle = TSL.atan(yComponent, xComponent)
            const normalizedAngle = TSL.mul(TSL.add(angle, Math.PI), 1 / (2 * Math.PI)) // Normalize the angle from [-PI, PI] to [0, 1]
            const color = TSL.mix(black, white, normalizedAngle).toColor()
            return {color, fac: normalizedAngle}
        }

        throw new Error(`Unhandled gradient type: ${type}`)
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<TexGradientSockets>> => {
        const gradientTextureNode: GradientTextureNode = {
            type: "gradient_texture",
            inputs: {vector: await evaluator.get(this.parameters.inputs.vector)},
            parameters: {gradient_type: cyclesGradientType(this.parameters.settings.gradientType)},
            resources: {},
        }

        return {color: [gradientTextureNode, "color"], fac: [gradientTextureNode, "fac"]}
    }
}
